import { useCallback, useEffect, useMemo, useState } from "react";
import { LoaderCircle, PackageOpen, PackagePlus, Share2, ShoppingBasket } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { GroceryAddToPantrySheet } from "@/components/GroceryAddToPantrySheet";
import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, getApiError } from "@/lib/api";
import { buildGroceryShareText, getShareSuccessMessage, shareContent } from "@/lib/share";
import { toast } from "@/components/ui/sonner";


const STOCK_BADGE_STYLES = {
  "In pantry": "bg-emerald-100 text-emerald-800",
  "Low stock": "bg-amber-100 text-amber-800",
  Out: "bg-rose-100 text-rose-800",
};

const FRIDGE_KEYWORDS = ["milk", "yogurt", "kwark", "skyr", "cheese", "kaas", "chicken", "salmon", "turkey", "meat", "gehakt", "tofu", "tempeh", "apple", "banana", "avocado", "tomato", "spinach", "berries"];
const PANTRY_KEYWORDS = ["rice", "pasta", "oats", "beans", "lentils", "chickpeas", "tomato paste", "vinegar", "oil", "nuts", "seeds"];
const FROZEN_KEYWORDS = ["frozen", "ijs", "ice", "vries"];

const requiresPantryConfirm = (item) => !item?.ingredient_id || !((item?.recommended_purchase_quantity || item?.pack_size) && (item?.pack_unit || item?.unit) && (item?.purchase_unit_label || item?.pack_label));

const inferInventoryDefaults = (item) => {
  const name = (item?.ingredient_name || "").toLowerCase();
  const category = (item?.category || "").toLowerCase();
  const packSize = item?.pack_size || item?.recommended_purchase_quantity || item?.quantity || 0;
  const unit = item?.pack_unit || item?.unit || "g";

  if (FROZEN_KEYWORDS.some((keyword) => name.includes(keyword))) {
    return { location: "freezer", status: "sealed" };
  }
  if (["dairy", "proteins", "vegetables", "fruit"].includes(category) || FRIDGE_KEYWORDS.some((keyword) => name.includes(keyword))) {
    return { location: "fridge", status: "fresh" };
  }
  if (["grains", "legumes", "nuts", "herbs"].includes(category) || PANTRY_KEYWORDS.some((keyword) => name.includes(keyword))) {
    const bulkLike = (["g", "ml"].includes(unit) && packSize >= 750) || (unit === "pcs" && packSize >= 8);
    return { location: bulkLike ? "stock" : "pantry", status: "sealed" };
  }
  return { location: "pantry", status: "sealed" };
};


export default function GroceryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [grocery, setGrocery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ quantity: "", unit: "g", location: "pantry", status: "sealed" });
  const [undoAction, setUndoAction] = useState(null);
  const weekStartParam = searchParams.get("week_start");

  const confirmItemDefaults = useMemo(() => {
    if (!confirmItem) {
      return { location: "pantry", status: "sealed" };
    }
    return inferInventoryDefaults(confirmItem);
  }, [confirmItem]);

  const loadGrocery = useCallback(async () => {
    setLoading(true);
    const response = await api.get("/grocery", {
      params: {
        week_start: weekStartParam || undefined,
      },
    });
    setGrocery(response.data);
    setLoading(false);
  }, [weekStartParam]);

  useEffect(() => {
    loadGrocery();
  }, [loadGrocery]);

  useEffect(() => {
    if (!undoAction) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setUndoAction(null);
    }, 6000);

    return () => window.clearTimeout(timeout);
  }, [undoAction]);

  const showUndoSnackbar = (responseData, message) => {
    if (responseData?.pantry_action?.id) {
      setUndoAction({
        actionId: responseData.pantry_action.id,
        ingredientName: responseData.pantry_action.ingredient_name,
        quantity: responseData.pantry_action.quantity,
        unit: responseData.pantry_action.unit,
        message,
      });
      return;
    }
    toast.success(message);
  };

  const toggleItem = async (itemId, checked) => {
    try {
      setPendingAction({ itemId, type: "check" });
      const response = await api.post(`/grocery/items/${itemId}/check`, { checked: Boolean(checked) });
      setGrocery(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setPendingAction(null);
    }
  };

  const addItemToPantry = async (itemId) => {
    try {
      setPendingAction({ itemId, type: "pantry" });
      const response = await api.post(`/grocery/items/${itemId}/add-to-pantry`);
      setGrocery(response.data);
      showUndoSnackbar(response.data, "Item toegevoegd aan pantry.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setPendingAction(null);
    }
  };

  const addItemToPantryWithConfirm = async () => {
    if (!confirmItem) {
      return;
    }

    try {
      setPendingAction({ itemId: confirmItem.id, type: "pantry-confirm" });
      const response = await api.post(`/grocery/items/${confirmItem.id}/add-to-pantry`, {
        quantity: Number(confirmForm.quantity),
        unit: confirmForm.unit,
        location: confirmForm.location,
        status: confirmForm.status,
      });
      if (response.data?.needs_confirmation) {
        toast.error("Nog niet genoeg productdata om automatisch toe te voegen.");
        return;
      }
      setGrocery(response.data);
      setConfirmItem(null);
      showUndoSnackbar(response.data, "Item bevestigd en toegevoegd aan pantry.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setPendingAction(null);
    }
  };

  const undoPantryAction = async () => {
    if (!undoAction?.actionId) {
      return;
    }

    try {
      setPendingAction({ itemId: undoAction.actionId, type: "undo" });
      const response = await api.post(`/grocery/pantry-actions/${undoAction.actionId}/undo`);
      setGrocery(response.data);
      setUndoAction(null);
      toast.success("Pantry-actie ongedaan gemaakt.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setPendingAction(null);
    }
  };

  const updateConfirmForm = (field, value) => {
    setConfirmForm((current) => ({ ...current, [field]: value }));
  };

  const openAddToPantryFlow = (item) => {
    if (!requiresPantryConfirm(item)) {
      addItemToPantry(item.id);
      return;
    }

    const defaults = inferInventoryDefaults(item);
    setConfirmItem(item);
    setConfirmForm({
      quantity: String(item?.recommended_purchase_quantity || item?.pack_size || item?.quantity || ""),
      unit: item?.pack_unit || item?.unit || "g",
      location: defaults.location,
      status: defaults.status,
    });
  };

  const shareGrocery = async () => {
    if (!grocery) {
      return;
    }

    try {
      const mode = await shareContent({
        title: `Smartly boodschappenlijst ${grocery.week_start}`,
        text: buildGroceryShareText(grocery),
        url: `${window.location.origin}/grocery?week_start=${grocery.week_start}`,
      });
      toast.success(getShareSuccessMessage(mode, "Boodschappenlijst"));
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error("Boodschappenlijst delen lukte niet.");
    }
  };

  return (
    <MobileShell title="Boodschappen" eyebrow="Compact per categorie" action={<Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={() => navigate("/pantry")} data-testid="grocery-open-pantry-button">Pantry</Button>}>
      <div className="space-y-4" data-testid="grocery-page">
        {loading ? (
          <section className="rounded-[28px] border border-stone-200 bg-white/90 p-5" data-testid="grocery-loading-state">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="grocery-loading-kicker">
              Boodschappenlijst laden
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-stone-200">
              <div className="h-full w-2/3 animate-pulse rounded-full bg-emerald-700" data-testid="grocery-loading-bar" />
            </div>
          </section>
        ) : null}

        <section className="rounded-[28px] border border-stone-200 bg-[linear-gradient(135deg,_rgba(243,230,198,0.9),_rgba(236,245,234,0.95))] p-4" data-testid="grocery-summary-card">
          <div className="flex items-start gap-3">
            <ShoppingBasket className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="grocery-summary-title">
                Lijst gegenereerd voor week {grocery?.week_start || "..."}
              </p>
              <p className="mt-2 text-sm text-stone-600" data-testid="grocery-summary-description">
                Volledig gedekte items blijven verborgen uit je lijst, maar tellen wel mee als In pantry bovenaan.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-white/85 px-3 py-1 text-stone-700" data-testid="grocery-to-buy-pill">{grocery?.item_count || 0} te kopen</span>
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="grocery-in-pantry-pill">{grocery?.summary?.in_pantry_count || 0} In pantry</span>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800" data-testid="grocery-low-stock-pill">{grocery?.summary?.low_stock_count || 0} Low stock</span>
                <span className="rounded-full bg-rose-100 px-3 py-1 text-rose-800" data-testid="grocery-out-pill">{grocery?.summary?.out_count || 0} Out</span>
                {grocery?.summary?.leftover_meal_count ? <span className="rounded-full bg-orange-100 px-3 py-1 text-orange-700" data-testid="grocery-leftover-meals-pill">{grocery.summary.leftover_meal_count} leftover-maaltijden</span> : null}
                <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid="grocery-ready-state">klaar</span>
              </div>
              <Button variant="outline" className="mt-4 rounded-full border-stone-200 bg-white" onClick={shareGrocery} data-testid="grocery-share-button">
                <Share2 className="mr-2 h-4 w-4" />
                Deel lijst
              </Button>
            </div>
          </div>
        </section>

        <section className="rounded-[28px] border border-dashed border-stone-300 bg-white/75 p-4" data-testid="grocery-pantry-card">
          <div className="flex items-start gap-3">
            <PackageOpen className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="grocery-pantry-title">
                Pantry subtract actief
              </p>
              <p className="mt-2 text-sm text-stone-600" data-testid="grocery-pantry-description">
                Smartly checkt pantry, stock, koelkast en vriezer. Alleen wat nog mist blijft zichtbaar in deze lijst.
              </p>
            </div>
          </div>
        </section>

        {!loading && !grocery?.groups?.length ? (
          <section className="rounded-[28px] border border-emerald-200 bg-emerald-50/90 p-5" data-testid="grocery-empty-state">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700" data-testid="grocery-empty-kicker">
              Alles gedekt
            </p>
            <p className="mt-2 text-lg font-semibold text-stone-900" data-testid="grocery-empty-title">
              Je hoeft deze week niets extra te kopen.
            </p>
            <p className="mt-2 text-sm text-stone-600" data-testid="grocery-empty-description">
              {grocery?.summary?.in_pantry_count || 0} items zijn al volledig aanwezig en blijven daarom uit de boodschappenlijst.
            </p>
          </section>
        ) : null}

        {grocery?.groups?.map((group, groupIndex) => (
          <section key={group.category} className="stagger-enter rounded-[28px] border border-stone-200 bg-white/90 p-4" style={{ animationDelay: `${groupIndex * 60 + 60}ms` }} data-testid={`grocery-group-${group.category}`}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid={`grocery-group-title-${group.category}`}>
                {group.category}
              </h2>
              <span className="rounded-full bg-stone-100 px-3 py-1 text-xs text-stone-600" data-testid={`grocery-group-count-${group.category}`}>
                {group.items.length} items
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {group.items.map((item) => (
                <div key={item.id} className="flex items-start gap-3 rounded-[22px] border border-stone-200 bg-stone-50 px-3 py-3" data-testid={`grocery-item-row-${item.id}`}>
                  <div className="pt-1">
                    <Checkbox checked={item.checked} disabled={pendingAction?.itemId === item.id} onCheckedChange={(checked) => toggleItem(item.id, checked)} data-testid={`grocery-item-checkbox-${item.id}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-stone-900" data-testid={`grocery-item-name-${item.id}`}>
                        {item.ingredient_name}
                      </p>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${STOCK_BADGE_STYLES[item.stock_status] || STOCK_BADGE_STYLES.Out}`} data-testid={`grocery-item-stock-badge-${item.id}`}>
                        {item.stock_status || "Out"}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-stone-500" data-testid={`grocery-item-quantity-${item.id}`}>
                      Nog {item.quantity} {item.unit} nodig
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-stone-500" data-testid={`grocery-item-stock-detail-${item.id}`}>
                      <span className={`rounded-full px-2.5 py-1 ${item.checked ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`} data-testid={`grocery-item-purchase-state-${item.id}`}>
                        {item.checked ? "Gekocht" : "Nog kopen"}
                      </span>
                      {item.pantry_used_quantity ? <span className="rounded-full bg-white px-2.5 py-1" data-testid={`grocery-item-pantry-used-${item.id}`}>{item.pantry_used_quantity} {item.unit} al in pantry</span> : null}
                      {item.pack_label ? <span className="rounded-full bg-white px-2.5 py-1" data-testid={`grocery-item-pack-label-${item.id}`}>Koop als {item.pack_label}</span> : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3 text-xs" disabled={pendingAction?.itemId === item.id} onClick={() => openAddToPantryFlow(item)} data-testid={`grocery-item-add-pantry-button-${item.id}`}>
                      {pendingAction?.itemId === item.id && ["pantry", "pantry-confirm"].includes(pendingAction?.type) ? <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" /> : <PackagePlus className="mr-2 h-3.5 w-3.5" />}
                      Naar pantry
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        <GroceryAddToPantrySheet
          open={Boolean(confirmItem)}
          item={confirmItem}
          form={{ ...confirmForm, location: confirmForm.location || confirmItemDefaults.location, status: confirmForm.status || confirmItemDefaults.status }}
          submitting={pendingAction?.itemId === confirmItem?.id && pendingAction?.type === "pantry-confirm"}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmItem(null);
            }
          }}
          onFormChange={updateConfirmForm}
          onSubmit={addItemToPantryWithConfirm}
        />

        {undoAction ? (
          <div className="sticky bottom-[calc(5rem+env(safe-area-inset-bottom))] z-20" data-testid="grocery-undo-snackbar-wrap">
            <div className="mx-auto flex max-w-md items-center justify-between gap-3 rounded-full border border-emerald-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur" data-testid="grocery-undo-snackbar">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-900" data-testid="grocery-undo-snackbar-title">
                  {undoAction.message}
                </p>
                <p className="truncate text-xs text-stone-500" data-testid="grocery-undo-snackbar-description">
                  {undoAction.ingredientName} · {undoAction.quantity} {undoAction.unit}
                </p>
              </div>
              <Button
                variant="outline"
                className="shrink-0 rounded-full border-emerald-200 bg-emerald-50 px-3 text-xs text-emerald-800"
                disabled={pendingAction?.type === "undo"}
                onClick={undoPantryAction}
                data-testid="grocery-undo-snackbar-button"
              >
                {pendingAction?.type === "undo" ? <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                Undo
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </MobileShell>
  );
}